import { XIcon } from 'lucide-react'
import type * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * A removable filter chip — the thing that sits in a grid's filter bar.
 *
 * Distinct from Badge: a Badge reports state and is not interactive; a Tag is a
 * filter the user applied and can take off. `removeLabel` is required whenever
 * `onRemove` is passed, because "✕" alone tells a screen-reader user nothing
 * about which of six filters they are about to clear.
 *
 * Paper with a `--border` edge and 4px control corners, 24px tall, 12px
 * weight 500: a control at rest, not a status (the prototype's `.chip`).
 */
function Tag({
  className,
  children,
  onRemove,
  removeLabel,
  ...props
}: React.ComponentProps<'span'> & {
  onRemove?: () => void
  removeLabel?: string
}) {
  return (
    <span
      data-slot="tag"
      className={cn(
        'inline-flex h-6 w-fit shrink-0 items-center gap-1.5 rounded-sm border border-border bg-card ps-2 pe-1',
        'text-xs font-medium whitespace-nowrap text-foreground',
        !onRemove && 'pe-2',
        className,
      )}
      {...props}
    >
      {children}
      {onRemove ? (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className="inline-flex size-4 items-center justify-center rounded-[2px] text-muted-foreground transition-ui hover:bg-accent hover:text-foreground"
        >
          <XIcon className="size-3" aria-hidden="true" />
        </button>
      ) : null}
    </span>
  )
}

export { Tag }
